import {
  Box,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { usePolicyOutletContext } from "../hooks/usePolicyOutletContext";
import {
  Access,
  ExceptionType,
  ListExceptionGroupsDocument,
  Tag,
} from "~/operations";
import { Exceptions } from "~/components/exceptions/exceptions";
import { Link } from "react-router-dom";
import { CheckStats } from "~/components/impact";
import { Header } from "~/types/table";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { Clamp } from "~/components/clamp/clamp";
import getQueryMrn from "~/lib/getQueryMrn";
import { FirewatchChip } from "~/components/FirewatchRow/FirewatchChip";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { spaceMrnFromExceptionGroup } from "~/lib/mrn";
import { spaceIdFromMrn } from "~/hooks/useScope";
import { usePolicyExceptionsFilterContext } from "~/pages/shared/policy/hooks/usePolicyExceptionsFilterContext";

const tableHeaders: Header[] = [
  { id: "IMPACT", label: "Impact" },
  { id: "SCORE", label: "Scores", options: { width: 150 } },
  { id: "QUERY", label: "Check" },
  {
    id: "ASSETS",
    label: "Assets",
    options: { textAlign: "right", width: 150 },
  },
];

export interface PolicyExceptionsTabProps {
  scopeMrn: string;
}

export function PolicyExceptionsTab({ scopeMrn }: PolicyExceptionsTabProps) {
  const { policy, policyId } = usePolicyOutletContext();

  const { filterForm, filterVariables } = usePolicyExceptionsFilterContext({
    scopeMrn,
  });

  const { exceptionGroups, loadingListExceptionGroup } = useFetchExceptions({
    mrn: policy.mrn,
    scopeMrn,
    types: [ExceptionType.Security],
    includeChildScopes: true,
    includeDeleted: true,
    includeExtension: true,
    filterVariables,
  });

  const isPrivatePolicy = policy.access === Access.Private;

  return (
    <>
      {filterForm}
      <Exceptions
        findingMrn={policy.mrn}
        exceptionTypes={[ExceptionType.Security]}
        isLoading={loadingListExceptionGroup}
        emptyStateType="check-exceptions"
        refetchQueries={[ListExceptionGroupsDocument]}
        exceptionGroups={exceptionGroups}
        filterVariables={filterVariables}
        hideViewSwitcher
        renderTable={(exceptionGroup) => {
          const { exceptions } = exceptionGroup;
          const spaceMrn = spaceMrnFromExceptionGroup(exceptionGroup);
          const spaceId = spaceIdFromMrn(spaceMrn);
          return (
            <>
              <Table
                sx={{
                  borderRadius: 1,
                  overflow: "hidden",
                  "& tr:last-child td": {
                    borderBottom: "none",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "background.lighter",
                      "&:hover": { backgroundColor: "background.lighter" },
                    }}
                  >
                    {tableHeaders.map((header: Header) => (
                      <TableCell key={header.id}>{header.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {exceptions.flatMap((exception) => {
                  if (
                    exception.__typename !== "SpaceCheckException" &&
                    exception.__typename !== "AssetCheckException"
                  )
                    return [];
                  const { mrn, riskValue, rating, title } = exception;

                  const isAssetException =
                    exception.__typename === "AssetCheckException";
                  const isSpaceException =
                    exception.__typename === "SpaceCheckException";

                  const policyNameTag: Tag = {
                    key: "policy",
                    value: exception.policyName,
                    __typename: "Tag",
                  };

                  const uid = mrn.split("/").pop();

                  let namespace = isPrivatePolicy ? spaceId : "mondoohq";

                  const checkMrn = getQueryMrn({
                    namespace,
                    uid: uid as string,
                  });

                  let assetId = "";
                  if (isAssetException) {
                    assetId = exceptionGroup.scopeMrn
                      .split("/")
                      .pop() as string;
                  }

                  const spaceExceptionLink = `/space/findings/check?spaceId=${spaceId}&findingMrn=${checkMrn}&policyId=${policyId}`;
                  const assetExceptionLink = `/space/inventory/${assetId}/check?checkMrn=${checkMrn}&spaceId=${spaceId}`;
                  const urlLink = isSpaceException
                    ? spaceExceptionLink
                    : assetExceptionLink;

                  return (
                    <TableRow
                      key={mrn}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: "background.light",
                      }}
                    >
                      <TableCell>
                        {rating && (
                          <ImpactUpdated
                            impact={{ rating, value: riskValue ?? 0 }}
                            isRiskScore
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {isSpaceException ? (
                          <CheckStats
                            results={{
                              passed: exception.assetsStats.passed,
                              failed: exception.assetsStats.failed,
                              errored: exception.assetsStats.errored,
                            }}
                          />
                        ) : (
                          "Not applicable"
                        )}
                      </TableCell>
                      <TableCell>
                        <Clamp>
                          <Typography
                            component={Link}
                            to={urlLink}
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              lineHeight: "16px",
                            }}
                          >
                            {title}
                          </Typography>
                        </Clamp>
                        <Box display="flex" gap={1} mt={1}>
                          <FirewatchChip
                            key={policyNameTag.key}
                            tag={policyNameTag}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {isSpaceException
                          ? exception.assetsStats.total
                          : "Not applicable"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Table>
            </>
          );
        }}
      />
    </>
  );
}
